import { computed, defineComponent, type FunctionalComponent } from 'vue'
import type { IProviderImportStepProps } from '../types/stepProps'
import Section from 'components/section'
import { Spinner } from 'components/spinner'
import { Form } from '@/form/useForm'
import Centered from 'components/centered'
import QRImage from 'components/qr-image'

const InstructionsComponent: FunctionalComponent = () => {
  return (
    <Section class="text-gray-500 text-sm font-normal text-center">
      1. Öppna BankID-appen i din mobil
      <br />
      2. Klicka på QR-kodknappen
      <br />
      3. Skanna QR-koden nedan
    </Section>
  )
}

const SecurityText: FunctionalComponent = () => {
  return (
    <div class="max-w-[525px] text-center text-gray-500 text-sm font-normal leading-[21px]">
      Securely connect your assets to get information about values, positions and transactions as
      Findex has read-only access. Read more about security{' '}
      <a
        class="text-primary-500 font-bold underline hover:text-primary-600"
        href="https://findex.se/security"
        target="_blank"
      >
        here
      </a>
      .
    </div>
  )
}

export const AuthenticateStep = defineComponent<IProviderImportStepProps>(
  (props) => {
    const didFinishScanning = computed(
      () =>
        !!props.hook.qrString.value &&
        !!props.hook.sidString.value &&
        !props.hook.isScanningQR.value
    )

    return () => {
      return (
        <div class="w-full h-full relative flex flex-col justify-center">
          {didFinishScanning.value ? (
            <div class="w-full h-full">
              {props.hook.userAccounts.value.length > 1 ? (
                <div class="space-y-4">
                  <div>
                    It looks like you have more than one user account, please select one below
                  </div>
                  <Form form={props.hook.form} />
                </div>
              ) : (
                <div />
              )}
            </div>
          ) : (
            <div
              class="space-y-4"
              style={{
                textAlign: 'center'
              }}
            >
              <img
                style={{ display: 'inline-block' }}
                class="w-[86px]"
                src={props.hook.provider.value?.iconUrl}
              />
              <div
                style={{ textAlign: 'center' }}
                class="text-gray-900 text-lg font-semibold font-['Inter'] leading-[18px]"
              >
                Connect to {props.hook.provider.value?.displayName}
              </div>
              {props.hook.isScanningQR.value ? (
                <InstructionsComponent />
              ) : (
                <div class="grid grid-auto-rows justify-center gap-6 px-4 md:px-0">
                  <SecurityText />
                  <Form form={props.hook.form} />
                </div>
              )}

              {props.hook.isScanningQR.value && (
                <div class="grid justify-center items-center">
                  <div class="w-40 h-40">
                    <Centered key={`qr-${props.hook.qrString.value}`}>
                      <div
                        style={{
                          width: '10rem',
                          height: '10rem'
                        }}
                      >
                        {props.hook.qrString.value ? (
                          // Render pre-rendered QR code image directly if it is base64 image data URI
                          props.hook.qrString.value.startsWith('data:image') ? (
                            <img
                              style={{ width: '100%', height: '100%', display: 'block' }}
                              src={props.hook.qrString.value}
                              alt="QR Code"
                            />
                          ) : (
                            <QRImage qrstring={props.hook.qrString.value} />
                          )
                        ) : (
                          <Spinner />
                        )}
                      </div>
                    </Centered>
                  </div>
                </div>
              )}
            </div>
          )}
          {props.hook.isFetchingUserAccounts.value && (
            <div class="w-full h-full grid items-center justify-center">
              <Spinner />
            </div>
          )}
        </div>
      )
    }
  },
  { props: ['hook'] }
)
